import {
  hasKey,
  hasLanguageKeyAndStringArrayValue,
  type TAvailableLanguageCodes,
  type TIntent,
} from '@hubertai/screening-question-types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { current, nanoid } from '@reduxjs/toolkit';
import { ECreateQuestionMode } from 'components/Questionsv2/builder/context/create-question.context';
import { EEvaluationType } from '@hubertai/screening-question-types';
import type { QuestionEditorsState } from './Question.Editor.Slice';

const getEmptyIntent = (language: TAvailableLanguageCodes, intent?: string): TIntent => {
  return {
    intent: intent ?? 'affirm',
    value: intent ?? 'affirm',
    replies: { [language]: ['utter_confirm', 'next'] },
    enabled: { [language]: true },
    evaluation: EEvaluationType.Info,
  };
};

export const questionIntentsReducer = {
  resetIntents: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const { questionId } = action.payload;
    if (state.byId[questionId]) {
      const currentQuestion = state.byId[questionId];
      const questionConfig = currentQuestion.questionConfig;
      const withNewIds = (items: any[]) =>
        [ECreateQuestionMode.Copy, ECreateQuestionMode.InTemplateCopy].includes(currentQuestion.mode)
          ? items.map((item) => ({ ...item, id: nanoid() }))
          : items;
      const originalIntents: TIntent[] = hasKey(questionConfig, 'intents') ? withNewIds(questionConfig.intents) : [];

      state.byId[questionId] = {
        ...currentQuestion,
        intents: [...currentQuestion.intents, ...originalIntents],
      };
    }
  },

  addReply: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      fieldIndex: number;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, fieldIndex, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.intents));

    if (!x || !x.length || typeof x[fieldIndex] === 'undefined') {
      if (!question.intentErrors) question.intentErrors = [];
      question.intentErrors.push('Something is wrong.');
      return;
    }

    // Ensure replies exist
    if (!hasKey(x[fieldIndex], 'replies') || !x[fieldIndex].replies) {
      if (!question.intentErrors) question.intentErrors = [];
      question.intentErrors.push('Replies object is missing.');
      return;
    }

    const replies = x[fieldIndex].replies;

    if (hasLanguageKeyAndStringArrayValue(replies, language)) {
      replies[language].splice(replies[language].length - 2, 0, 'utter_confirm');
    } else {
      replies[language] = ['utter_confirm'];
    }
    question.intents = [...x];
    question.stateModified = true;
  },

  removeReply: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      fieldIndex: number;
      itemIndex: number;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, fieldIndex, itemIndex, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.intents));

    if (!x || !x.length || typeof x[fieldIndex] === 'undefined') {
      if (!question.intentErrors) question.intentErrors = [];
      question.intentErrors.push('Something is wrong.');
      return;
    }

    // Ensure replies exist
    if (!hasKey(x[fieldIndex], 'replies') || !x[fieldIndex].replies) {
      if (!question.intentErrors) question.intentErrors = [];
      question.intentErrors.push('Replies object is missing.');
      return;
    }

    const replies = x[fieldIndex].replies;

    if (hasLanguageKeyAndStringArrayValue(replies, language)) {
      replies[language].splice(itemIndex, 1);
      question.intents = [...x];
      question.stateModified = true;
    }
  },

  editReply: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      fieldIndex: number;
      itemIndex: number;
      newReply: string;
      language: TAvailableLanguageCodes;
    }>,
  ) => {
    const { questionId, fieldIndex, itemIndex, newReply, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.intents));

    if (!x || !x.length || typeof x[fieldIndex] === 'undefined') {
      if (!question.intentErrors) question.intentErrors = [];
      question.intentErrors.push('Something is wrong.');
      return;
    }

    // Ensure replies exist
    if (!hasKey(x[fieldIndex], 'replies') || !x[fieldIndex].replies) {
      if (!question.intentErrors) question.intentErrors = [];
      question.intentErrors.push('Replies object is missing.');
      return;
    }

    const replies = x[fieldIndex].replies;

    if (hasLanguageKeyAndStringArrayValue(replies, language)) {
      replies[language][itemIndex] = newReply;
      question.intents = [...x];
      question.stateModified = true;
    }
  },

  addIntent: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      language: TAvailableLanguageCodes;
      intent?: string;
    }>,
  ) => {
    const { questionId, intent, language } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = structuredClone(current(question.intents));
    x.push(getEmptyIntent(language, intent));

    question.intents = [...x];
    question.stateModified = true;
  },

  updateIntent: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      index: number;
      field: string;
      value: any;
    }>,
  ) => {
    const { questionId, index, field, value } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.intents];

    if (field === 'intent') {
      x[index].intent = value;
      x[index].value = value;
    } else {
      // @ts-ignore
      x[index][field] = value;
    }
    question.intents = [...x];
    question.stateModified = true;
  },

  removeIntent: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      index: number;
    }>,
  ) => {
    const { questionId, index } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const x = [...question.intents];
    x.splice(index, 1);
    console.log('index', index, x);
    question.intents = [...x];
    question.stateModified = true;
  },

  updateIntentEnabled: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      language: TAvailableLanguageCodes;
      intentIndex: number;
      enabled: boolean;
    }>,
  ) => {
    const { questionId, language, intentIndex, enabled } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const intents = structuredClone(current(question.intents));
    intents[intentIndex].enabled[language] = enabled;
    question.intents = [...intents];
    question.stateModified = true;
  },
};
